import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { candidateService } from '../../../services/candidateService';

const CandidateHeader = ({ 
  candidate, 
  application,
  onScheduleInterview, 
  onSendMessage, 
  onMoveStage,
  onCandidateUpdate 
}) => {
  const [isHiring, setIsHiring] = useState(false);
  const [error, setError] = useState(null);

  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-blue-100 text-blue-800',
      'applied': 'bg-blue-100 text-blue-800',
      'screening': 'bg-yellow-100 text-yellow-800',
      'interview': 'bg-purple-100 text-purple-800',
      'final_review': 'bg-orange-100 text-orange-800',
      'offer': 'bg-green-100 text-green-800',
      'hired': 'bg-green-200 text-green-900 font-bold',
      'rejected': 'bg-red-100 text-red-800'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800';
  };

  const handleHireCandidate = async () => {
    if (!application?.id) {
      setError('No application found for this candidate');
      return;
    }

    if (application?.stage === 'hired') {
      setError('Candidate is already hired');
      return;
    }

    setIsHiring(true);
    setError(null);

    try {
      const { data, error: hireError } = await candidateService?.hireCandidate(application?.id);
      
      if (hireError) {
        setError(hireError?.message || 'Failed to hire candidate');
        return;
      }

      // Update the application state in parent component
      if (onCandidateUpdate) {
        onCandidateUpdate(data);
      }

      // Show success message
      setError(null);
    } catch (err) {
      console.error('Hire candidate error:', err);
      setError('Failed to hire candidate. Please try again.');
    } finally {
      setIsHiring(false);
    }
  };

  // Display current application stage or fallback
  const currentStage = application?.stage || candidate?.status || 'applied';
  const isHired = currentStage === 'hired';

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      {/* Error message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
        {/* Left Section - Photo and Basic Info */}
        <div className="flex flex-col sm:flex-row items-start gap-4">
          <div className="w-24 h-24 rounded-lg overflow-hidden bg-muted flex-shrink-0">
            <Image
              src={candidate?.photo || candidate?.resume_url}
              alt={`${candidate?.full_name || candidate?.name || 'Candidate'} profile photo`}
              className="w-full h-full object-cover"
            />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
              <h1 className="text-2xl font-semibold text-foreground">
                {candidate?.full_name || candidate?.name || 'Candidate Name'}
              </h1>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(currentStage)}`}>
                {currentStage === 'hired' ? '🎉 HIRED' : currentStage?.replace('_', ' ')?.toUpperCase()}
              </span>
            </div>
            
            <p className="text-lg text-muted-foreground mb-3">
              {application?.jobs?.title || candidate?.position || 'Position Title'}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Icon name="Mail" size={16} />
                <span>{candidate?.email || 'No email provided'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Phone" size={16} />
                <span>{candidate?.phone || 'No phone provided'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="MapPin" size={16} />
                <span>{candidate?.location || 'Location not specified'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Section - Actions */}
        <div className="flex flex-col sm:flex-row lg:flex-col gap-2 lg:min-w-[200px]">
          {/* Hire Candidate Button - Only show if not already hired */}
          {!isHired && (
            <Button
              variant="default"
              iconName="UserCheck"
              iconPosition="left"
              onClick={handleHireCandidate}
              disabled={isHiring}
              className="w-full sm:w-auto lg:w-full bg-green-600 hover:bg-green-700 text-white"
            >
              {isHiring ? 'Hiring...' : 'Hire Candidate'}
            </Button>
          )}

          {/* Show celebration message if hired */}
          {isHired && (
            <div className="w-full sm:w-auto lg:w-full bg-green-50 border border-green-200 rounded-lg p-3 text-center">
              <span className="text-green-800 font-semibold">🎉 Candidate Hired!</span>
            </div>
          )}
          
          <Button
            variant="default"
            iconName="Calendar"
            iconPosition="left"
            onClick={onScheduleInterview}
            className="w-full sm:w-auto lg:w-full"
          >
            Schedule Interview
          </Button>
          
          <Button
            variant="outline"
            iconName="MessageSquare"
            iconPosition="left"
            onClick={onSendMessage}
            className="w-full sm:w-auto lg:w-full"
          >
            Send Message
          </Button>
          
          <Button
            variant="secondary"
            iconName="ArrowRight"
            iconPosition="left"
            onClick={onMoveStage}
            className="w-full sm:w-auto lg:w-full"
          >
            Move to Next Stage
          </Button>
        </div>
      </div>
      {/* Application Details */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Applied Date:</span>
            <p className="font-medium text-foreground mt-1">
              {candidate?.created_at ? 
                new Date(candidate.created_at)?.toLocaleDateString() : 
                candidate?.appliedDate || 'Not specified'
              }
            </p>
          </div>
          <div>
            <span className="text-muted-foreground">Source:</span>
            <p className="font-medium text-foreground mt-1">
              {candidate?.source || candidate?.experience || 'Direct application'}
            </p>
          </div>
          <div>
            <span className="text-muted-foreground">Department:</span>
            <p className="font-medium text-foreground mt-1">
              {application?.jobs?.department || candidate?.expectedSalary || 'Not specified'}
            </p>
          </div>
          <div>
            <span className="text-muted-foreground">Stage:</span>
            <p className="font-medium text-foreground mt-1">
              {currentStage?.replace('_', ' ')?.toUpperCase()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateHeader;